"use client";

import { useRef, useState } from "react";

import type { HeaderProps } from "./types/header.types";
import { headerStyles, headerContainerStyles } from "./styles/header.styles";

import { HeaderProvider, useHeaderContext } from "./header.context";
import { useIsomorphicLayoutEffect } from "../../hooks";

import { HeaderLogo } from "./header-logo";
import { HeaderActions } from "./header-actions";
import { HeaderNavbar } from "./header-navbar";
import { HeaderMobileMenu } from "./header-mobile-menu";
import { HeaderMobileMenuToggle } from "./header-mobile-menu-toggle";
import { HeaderMobileMenuFooter } from "./header-mobile-menu-footer";

type HeaderComponents = {
  Logo: typeof HeaderLogo;
  Actions: typeof HeaderActions;
  Navbar: typeof HeaderNavbar;
  MobileMenu: typeof HeaderMobileMenu;
  MobileMenuToggle: typeof HeaderMobileMenuToggle;
  MobileMenuFooter: typeof HeaderMobileMenuFooter;
};

const HeaderInner: React.FC<Omit<HeaderProps, "onMenuOpenChange">> = ({ children, className, style, ...rest }) => {
  const { menuOpen } = useHeaderContext();

  // The mobile menu is a fixed-position overlay that must begin exactly at the header's bottom edge.
  // Historically that offset was hardcoded to the header's own height (`top-64`), which assumes the
  // header sits flush against the top of the viewport. When anything renders above the header — most
  // commonly a promo `Banner` — the sticky header is pushed down and the menu would open *over* it.
  // The offset can't be known statically (a banner may be absent, and its height varies), so we
  // measure the header's actual on-screen bottom when the menu opens and publish it as the
  // `--mds-header-menu-top` custom property, which the menu and its scrim position against.
  // Measuring at open time is sufficient because opening the menu locks body scroll, so the header
  // can't move while the menu is visible. Falls back to the header-height token before measurement
  // (and during SSR), preserving the previous behavior when nothing sits above the header.
  const headerRef = useRef<HTMLElement>(null);
  const [menuTop, setMenuTop] = useState<number | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (!menuOpen) {
      setMenuTop(null);
      return;
    }
    const measure = () => {
      const el = headerRef.current;
      if (el) setMenuTop(Math.round(el.getBoundingClientRect().bottom));
    };
    // Measured synchronously (unthrottled) so the offset is correct before the menu's first paint.
    // The resize listener only matters for device rotation while the menu is already open, so it's
    // throttled to one measurement per frame to avoid layout thrash during continuous resize.
    measure();
    const onResize = () => requestAnimationFrame(measure);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [menuOpen]);

  const styleWithMenuTop = {
    ...style,
    "--mds-header-menu-top": menuTop != null ? `${menuTop}px` : "var(--spacing-header-height)",
  } satisfies React.CSSProperties & Record<`--${string}`, string>;

  return (
    <header {...rest} ref={headerRef} className={headerStyles({ className, menuOpen })} style={styleWithMenuTop}>
      <div className={headerContainerStyles()}>{children}</div>
    </header>
  );
};

export const Header: React.FC<HeaderProps> & HeaderComponents = (props) => {
  // `menuId` is destructured out rather than spread: it configures the menu's id, and React would
  // otherwise pass it through to the `<header>` element as an unknown attribute.
  const { children, className, onMenuOpenChange, menuId, ...rest } = props;

  return (
    <HeaderProvider onMenuOpenChange={onMenuOpenChange} menuId={menuId}>
      <HeaderInner className={className} {...rest}>
        {children}
      </HeaderInner>
    </HeaderProvider>
  );
};

Header.displayName = "Header";

Header.Logo = HeaderLogo;
Header.Actions = HeaderActions;
Header.Navbar = HeaderNavbar;
Header.MobileMenu = HeaderMobileMenu;
Header.MobileMenuToggle = HeaderMobileMenuToggle;
Header.MobileMenuFooter = HeaderMobileMenuFooter;
